import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet } from 'react-router-dom';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Users, 
  Package, 
  Gavel, 
  LogOut, 
  Menu, 
  X,
  Shield,
  BarChart3,
  Settings,
  Megaphone,
  PlusSquare,
  Layers
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create a separate axios instance for admin requests
const adminAxios = axios.create();

export default function AdminLayout() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    // Check for admin token (completely separate from main app)
    const adminToken = localStorage.getItem('admin_token');

    if (!adminToken) {
      navigate('/admin/login');
      return;
    }

    // Set admin token for admin axios instance
    adminAxios.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;

    try {
      // Verify admin access using separate admin endpoint
      const response = await adminAxios.get(`${API_URL}/api/admin/auth/me`);
      setAdminUser(response.data);
      
      // Also set the token for regular axios (for admin API calls)
      axios.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
    } catch (error) {
      console.error('Admin auth error:', error);
      // Clear invalid admin session
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // Clear admin session only
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    
    // Remove admin authorization
    delete adminAxios.defaults.headers.common['Authorization'];
    
    // Restore main app token if it exists
    const mainToken = localStorage.getItem('token');
    if (mainToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${mainToken}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
    
    navigate('/admin/login');
  };

  const navItems = [
    { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
    { name: 'Users', path: '/admin/users', icon: Users },
    { name: 'Listings', path: '/admin/listings', icon: Package },
    { name: 'Create Listings', path: '/admin/create-listings', icon: PlusSquare },
    { name: 'Bids', path: '/admin/bids', icon: Gavel },
    { name: 'Ads', path: '/admin/ads', icon: Megaphone },
    { name: 'Categories', path: '/admin/categories', icon: Layers },
    { name: 'Analytics', path: '/admin/analytics', icon: BarChart3 },
    { name: 'Settings', path: '/admin/settings', icon: Settings },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!adminUser) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Sidebar */}
      <aside 
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-slate-800 border-r border-slate-700 transition-all duration-300 flex flex-col`}
      >
        {/* Logo */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            {sidebarOpen && (
              <div>
                <h1 className="text-white font-bold">B2B Admin</h1>
                <p className="text-xs text-slate-400">Management Portal</p>
              </div>
            )}
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-slate-400 hover:text-white"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {navItems.map((item) => (
              <li key={item.name}>
                <Link
                  to={item.disabled ? '#' : item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    item.disabled 
                      ? 'text-slate-600 cursor-not-allowed'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  } ${
                    window.location.pathname === item.path ? 'bg-indigo-600 text-white' : ''
                  }`}
                  onClick={(e) => item.disabled && e.preventDefault()}
                  data-testid={`admin-nav-${item.name.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  {sidebarOpen && (
                    <span>
                      {item.name}
                      {item.disabled && <span className="text-xs ml-2">(Coming Soon)</span>}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold">{adminUser?.name?.[0]?.toUpperCase()}</span>
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-white font-medium truncate">{adminUser?.name}</p>
                  <span className="px-1.5 py-0.5 bg-indigo-500/30 text-indigo-300 text-xs rounded">
                    {adminUser?.role || 'Admin'}
                  </span>
                </div>
                <p className="text-xs text-slate-400 truncate">{adminUser?.email}</p>
              </div>
            )}
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-2 text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span>Logout</span>}
          </button>
          <a
            href="/"
            className="w-full flex items-center gap-3 px-4 py-2 mt-2 text-slate-400 hover:bg-slate-700 rounded-xl transition-colors"
          >
            <Package className="w-5 h-5" />
            {sidebarOpen && <span>Back to App</span>}
          </a>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
